import React, { createContext } from 'react'
import PropTypes from 'prop-types'
import ListingServices from '../Services/Consumers/ListingServices'
import { useIntl } from 'react-intl'
import { arrangeCodeData, handleNotificationAlert } from '../utils/helpers'
import { useKeycloak } from '@react-keycloak/web'
import { getApprovalCodeEditPayload } from '../Services/Payloads/ListingPayload'
import { read, utils, writeFileXLSX } from 'xlsx'
import CookiesStorageService from '../Services/CookiesStorageService'
import moment from 'moment'

const SingleCodeContext = createContext({})

const SingleCodeProvider = ({ shouldUpdate, children }) => {
  const intl = useIntl()
  const { keycloak } = useKeycloak()
  const [isCodeDialogOpen, setIsCodeDialogOpen] = React.useState(false)
  const [currentCodeLoading, setCurrentCodeLoading] = React.useState(false)
  const [currentCodeData, setCurrentCodeData] = React.useState({})

  const loadCodeData = (type, code, requesterTaxpayer, ownerTaxpayer, status, reuse, noClear, codeListingData) => {
    if (!noClear) {
      setCurrentCodeData({})
    }
    if (['Submitted'].includes(status)) {
      setCurrentCodeData(arrangeCodeData(codeListingData, requesterTaxpayer, ownerTaxpayer, status, reuse))
    } else if (!!keycloak?.authenticated && !!type && code) {
      setCurrentCodeLoading(true)
      ListingServices.getCodeData(type, code, intl.formatMessage({ id: 'singleCode.Error' })
      ).then((result) => {
        if (!!result && !!result.data) {
          setCurrentCodeData(arrangeCodeData(result.data, requesterTaxpayer, ownerTaxpayer, status, reuse))
        }
      }).then(() => {
        setCurrentCodeLoading(false)
      })
    }
  }

  const getCurrentCodeDataFromListing = (currentSelectedRow) => {
    setCurrentCodeData({})
    if (!!Object.keys(currentSelectedRow || {}).length) {
      setCurrentCodeData(arrangeCodeData(currentSelectedRow))
    }
  }
  const clearCodeData = () => {
    setCurrentCodeData({})
    setBulkImportResponse(null)
  }

  const updateCode = (values, FormikActions, closeConfirmDialog, codeUsageUniqueID, requestType, codeUsageRequestID, isApproved, updateParentEntity) => {
    if (!!keycloak?.authenticated) {
      ListingServices.handleDialogSubmit(!!isApproved ? 'codeUsageEditEGS' : 'codeUsageEdit', 'edit', FormikActions?.setSubmitting, FormikActions,
        getApprovalCodeEditPayload(values, !!isApproved), !!isApproved ? codeUsageUniqueID : codeUsageRequestID,
        intl.formatMessage({ id: 'SingleCode.Error.edit' }, { code: codeUsageUniqueID }), null, intl.formatMessage({ id: `listing.Error.401.action.codeRequests` })).then((result) => {
        if (!!result && ((!!result.data && !!result.data.data) || ['200', '201', '204'].includes(result.status.toString()))) {
          handleNotificationAlert({
            type: 'done',
            message: intl.formatMessage({ id: 'SingleCode.Success.edit' }, { code: codeUsageUniqueID }),
            color: 'success'
          }, 'success')
          if (!!FormikActions) {
            FormikActions.setSubmitting(false)
          }
          if (!!shouldUpdate) {
            shouldUpdate(true)
          }
          if (!!updateParentEntity) {
            setTimeout(() => updateParentEntity(true), 500)
          } else {
            setTimeout(() => cancelCodeDialog(), 200)
            setTimeout(() => loadCodeData('EGS', codeUsageUniqueID, null, null, null, null, true), 500)
          }
          /** Reload Listing after Add/Edit/Delete**/
        }
      }).then(() => {
        if (!!FormikActions) {
          FormikActions.setSubmitting(false)
        }
      })
    }
  }

  const getExactCodeObjects = (codes, type) => {
    let codeStructure = {
      codeUsage: {
        codetype: 'codeType',
        itemcode: 'itemCode',
        comment: 'comment'
      },
      EGSCodeUsage: {
        codetype: 'codeType',
        itemcode: 'itemCode',
        codename: 'codeName',
        codename2ar: 'codeNameAr',
        codenamear: 'codeNameAr',
        description: 'description',
        descriptionar: 'descriptionAr',
        gpcitemlinked: 'parentCode',
        activefrom: 'activeFrom',
        activeto: 'activeTo',
        egsrelatedcode: 'linkedCode'
      }
    }
    return codes?.map(item => Object.keys(item)?.reduce((obj, b) => {
      if (!!Object.keys(codeStructure[type])?.includes(b?.toLowerCase())) {
        obj[codeStructure[type][b?.toLowerCase()]] = ['activefrom', 'activeto'].includes(b?.toLowerCase()) ? moment(item[b]).locale('en-us').utc().format() : item[b]
      }
      return obj
    }, {}))?.filter(item => !!Object.keys(item || {})?.length)
  }
  const [bulkImportResponse, setBulkImportResponse] = React.useState(null)

  /**
   * Set the width of all columns of the given worksheet,
   * to fit the content of the cells.
   */
  const autoFitColumns = (worksheet) => {
    const [firstCol, lastCol] = worksheet['!ref']?.replace(/\d/, '').split(':')

    const numRegexp = new RegExp(/\d+$/g)

    const firstColIndex = firstCol.charCodeAt(0),
      lastColIndex = lastCol.charCodeAt(0),
      rows = +numRegexp.exec(lastCol)[0]

    const objectMaxLength = []

    // Loop on columns
    for (let colIndex = firstColIndex; colIndex <= lastColIndex; colIndex++) {
      const col = String.fromCharCode(colIndex)
      let maxCellLength = 0

      // Loop on rows
      for (let row = 1; row <= rows; row++) {
        const cellLength = worksheet[`${col}${row}`].v.length + 1
        if (cellLength > maxCellLength) maxCellLength = cellLength
      }

      objectMaxLength.push({ width: Math.min(maxCellLength, 45) })
    }
    worksheet['!cols'] = objectMaxLength
    return worksheet
  }

  const exportBulkCodeResponse = (codes, errors, type) => {
    let newData = []
    errors.forEach(error => {
      newData.push({ ...codes[error.index], 'Response': error.errors.join(', ') })
    })
    const ws = utils.json_to_sheet(newData)
    const wb = utils.book_new()
    utils.book_append_sheet(wb, autoFitColumns(ws), 'Response')
    writeFileXLSX(wb, type === 'EGSCodeUsage' ? 'NewCodeBulkResponseTemplate.xlsx' : 'CodeUsageBulkResponseTemplate.xlsx')
  }

  const bulkImportCodes = async (values, FormikActions, updateParentEntity) => {
    if (!!keycloak?.authenticated) {
      const codesFile = await values.codesFile.arrayBuffer()
      const wb = read(codesFile) // parse the array buffer
      const worksheet = wb.Sheets[wb.SheetNames[0]] // get the first worksheet
      const codes = utils.sheet_to_json(worksheet, { raw: false }) // generate objects
      const codesArray = getExactCodeObjects(codes?.map(item => {
        if (item?.CodeType === 'EGS') {
          let separated = item?.ItemCode?.toString().split('-')
          if (separated?.length < 3 || separated[0] !== 'EG') {
            item.ItemCode = `EG-${CookiesStorageService.getTaxpayerTRN()}-${item?.ItemCode}`
          }
        }
        return item
      }), values.bulkType)
      if (!!codesArray?.length) {
        ListingServices.handleDialogSubmit(values.bulkType === 'EGSCodeUsage' ? 'codeUsageEdit' : 'codesUsageCreate', 'create', FormikActions?.setSubmitting, FormikActions,
          { 'items': codesArray }, null, intl.formatMessage({ id: 'BulkCode.Error.create' })).then((result) => {
          if (!!result && ((!!result.data && !!result.data.data) || ['200', '201', '204'].includes(result.status.toString()))) {
            setBulkImportResponse({
              codesCount: codesArray?.length || 0,
              errorsCount: result?.data?.failedItems?.length || 0,
              passedCount: result?.data?.passedItemsCount || 0,
              exportResponse: !!result?.data?.failedItems?.length ? () => exportBulkCodeResponse(codes, result?.data?.failedItems, values.bulkType) : null
            })
            if (!!FormikActions) {
              FormikActions.setSubmitting(false)
            }
            if (!!shouldUpdate && !!result?.data?.passedItemsCount) {
              shouldUpdate(true)
            }
          }
        }).then(() => {
          if (!!FormikActions) {
            FormikActions.setSubmitting(false)
          }
        })
      } else {
        // if (!!shouldUpdate) {
        //   shouldUpdate(true)
        // }
        // if (!!updateParentEntity) {
        //   setTimeout(() => updateParentEntity(true), 500)
        // } else {
        //   setTimeout(() => cancelCodeDialog(), 200)
        // }
        handleNotificationAlert({
          type: 'defence',
          message: intl.formatMessage({ id: 'validationSchema.codesBulkImport.Codes' }),
          color: 'error'
        }, 'error')
        // if (!!FormikActions) {
        //   setTimeout(() => FormikActions.setSubmitting(false), 500)
        // }
      }

    }
  }

  const openCodeDialog = () => {
    setIsCodeDialogOpen(!isCodeDialogOpen)
  }

  const cancelCodeDialog = () => {
    setIsCodeDialogOpen(false)
    setBulkImportResponse(null)
  }
  return (
    <SingleCodeContext.Provider value={{
      currentCodeLoading,
      currentCodeData,

      isCodeDialogOpen,
      openCodeDialog,
      cancelCodeDialog,

      loadCodeData,
      updateCode,
      clearCodeData,

      getCurrentCodeDataFromListing,

      bulkImportCodes,
      bulkImportResponse

    }}>
      {children}
    </SingleCodeContext.Provider>
  )
}

SingleCodeProvider.propTypes = {
  shouldUpdate: PropTypes.func,
  children: PropTypes.any
}
const SingleCodeConsumer = SingleCodeContext.Consumer

export { SingleCodeProvider, SingleCodeConsumer }
